import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  Skeleton,
} from '@mui/material';
import {
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  Timeline as TimelineIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dashboardService from '../services/dashboardService';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.userType === 1;
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, activitiesResponse] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getRecentActivities(8)
      ]);
      setStats(statsResponse.data.stats);
      setActivities(activitiesResponse.data.activities);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatsCards = () => {
    if (!stats) return [];
    
    const cards = [
      {
        title: 'AMC Records',
        value: stats.totalAMCs,
        icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
        color: '#388e3c',
        change: `+${stats.monthlyAMCs} this month`,
        path: '/amc'
      },
      {
        title: 'Delivery Challans',
        value: stats.totalChallans,
        icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
        color: '#f57c00',
        change: `+${stats.monthlyChallans} this month`,
        path: '/delivery-challan'
      }
    ];
    
    if (isAdmin) {
      cards.unshift({
        title: 'Total Colleges',
        value: stats.totalColleges,
        icon: <SchoolIcon sx={{ fontSize: 40 }} />,
        color: '#1976d2',
        change: '+12%',
        path: '/colleges'
      });
    }
    
    return cards;
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome to Rayansh Securities Management System
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {loading ? (
          Array.from({ length: isAdmin ? 4 : 2 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={isAdmin ? 3 : 6} key={index}>
              <Card sx={{ height: 140 }}>
                <CardContent>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width="60%" sx={{ mt: 2 }} />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          getStatsCards().map((stat, index) => (
            <Grid item xs={12} sm={6} md={isAdmin ? 3 : 6} key={index}>
              <Card 
                sx={{ 
                  height: 140,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => navigate(stat.path)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: stat.color }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {stat.title}
                      </Typography>
                      <Chip 
                        label={stat.change} 
                        size="small" 
                        sx={{ 
                          mt: 1, 
                          bgcolor: `${stat.color}15`, 
                          color: stat.color,
                          fontSize: '0.7rem'
                        }} 
                      />
                    </Box>
                    <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, width: 56, height: 56 }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <TimelineIcon sx={{ mr: 1 }} />
                Recent Activities
              </Typography>
              
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  </Box>
                ))
              ) : activities.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {activities.map((activity, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon>
                        <Avatar sx={{ 
                          bgcolor: activity.type === 'AMC' ? '#388e3c15' : '#f57c0015',
                          color: activity.type === 'AMC' ? '#388e3c' : '#f57c00',
                          width: 40,
                          height: 40
                        }}>
                          {activity.type === 'AMC' ? <AssignmentIcon /> : <ReceiptIcon />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activity.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {activity.client || activity.college} • {activity.createdBy}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(activity.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                      {activity.amount && (
                        <Chip 
                          label={`₹${activity.amount.toLocaleString()}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No recent activities to display.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Quick Actions
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  ...(isAdmin ? [{ title: 'Manage Users', icon: <PersonIcon />, path: '/users', color: '#9c27b0' }] : []),
                  { title: 'Create AMC', icon: <AssignmentIcon />, path: '/amc', color: '#388e3c' },
                  ...(isAdmin ? [{ title: 'New Challan', icon: <ReceiptIcon />, path: '/delivery-challan', color: '#f57c00' }] : [])
                ].map((action, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    startIcon={action.icon}
                    onClick={() => navigate(action.path)}
                    sx={{
                      justifyContent: 'flex-start',
                      p: 2,
                      borderColor: `${action.color}30`,
                      color: action.color,
                      '&:hover': {
                        borderColor: action.color,
                        bgcolor: `${action.color}10`
                      }
                    }}
                  >
                    {action.title}
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
};

export default Dashboard;